// La pestaña MOODBOARDS de la biblioteca del Agente: las carpetas de referencias de la
// marca, para verlas, armarlas y —lo que la biblioteca sabe hacer— engancharlas de un
// toque como referencia de estilo del mensaje
// (ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente).
//
// Dos niveles, como en Mi marca: las carpetas (la MISMA `GrillaDeCarpetas`, con crear,
// renombrar, color y borrar) y adentro las fotos. La vista de adentro NO es la de Mi marca
// (`VistaDeCarpeta`): allá tocar una foto la abre en el visor; acá tocarla la ADJUNTA y se
// va volando a la caja, con la misma ficha, el mismo reacomodo y el mismo "lo adjunto no
// está en la grilla" que el resto de las pestañas. Agregar fotos y borrarlas sí están,
// como allá; el visor no, porque acá la foto se usa, no se mira.
//
// Es el ÚNICO que habla con `moodboardsApi` desde el chat, con el mismo reparto que
// `MiMarcaMoodboardsTab`: la grilla dibuja y avisa, éste es el dueño de los datos. Las
// fotos se firman DOS veces al abrir una carpeta: la miniatura con `transform` para la
// grilla (lo que ya hace `fetchFotos`) y el original en lote (`firmarEnLote`) para el
// adjunto — la firma del original es la que el motor renueva antes de cada turno; la de
// la miniatura, con transformación, no la reconoce (`leerFirma`).

import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ImagePlus, Loader2, Paperclip, Plus, Trash2 } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import { ModalConfirmar } from '../../components/ModalConfirmar';
import GrillaDeCarpetas from '../../components/moodboards/GrillaDeCarpetas';
import { AccionCard } from '../../pages/v2/components/AccionCard';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import {
  cambiarColorDeMoodboard,
  crearMoodboard,
  eliminarFoto,
  eliminarMoodboard,
  fetchFotos,
  fetchMoodboards,
  MOODBOARDS_BUCKET,
  renombrarMoodboard,
  subirFoto,
  type Moodboard,
  type MoodboardFoto,
} from '../../pages/v2/moodboardsApi';
import { firmarEnLote } from '../../utils/firmarEnLote';
import { adjuntoDeCarpetaDeMoodboard, adjuntoDeMoodboard, yaAdjunto } from './bibliotecaDeAdjuntos';
import { ATRIBUTO_FICHA, useReacomodoSuave } from './reacomodoDeGrilla';

interface Props {
  companyId: string | null;
  /** La marca activa (desempate canónico). `null` = todas las de la empresa. */
  brandId: string | null;
  /** Hasta que la marca no resolvió, `brandId` en `null` no significa "todas". */
  marcaResuelta: boolean;
  usuarioId: string | null;
  /** Lo que ya está en la caja: esas fotos no se muestran. */
  adjuntos: ChatAttachment[];
  /** Tocar una foto: el panel la engancha y la hace volar (mide `miniatura` antes). */
  onTomar: (
    ficha: { adjunto: ChatAttachment; imageUrl: string },
    miniatura: HTMLElement | null,
  ) => void;
  /** Se creó, renombró o borró una carpeta, o cambió cuántas fotos tiene: el catálogo
   *  que el agente lleva en el prompt se vuelve a pedir. */
  onCarpetasCambiadas?: () => void;
}

/** La grilla de la biblioteca: tres por fila, con el aire de las demás pestañas. */
const GRILLA = 'grid grid-cols-3 gap-x-3 gap-y-4';

/** Sólo imágenes: lo demás no es una referencia visual. */
const FORMATOS = 'image/*,.heic,.heif';

const contador = (n: number) => (n === 0 ? 'Sin fotos' : n === 1 ? '1 foto' : `${n} fotos`);

export const BibliotecaMoodboards: React.FC<Props> = ({
  companyId,
  brandId,
  marcaResuelta,
  usuarioId,
  adjuntos,
  onTomar,
  onCarpetasCambiadas,
}) => {
  const [carpetas, setCarpetas] = useState<Moodboard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<Moodboard | null>(null);
  const [fotos, setFotos] = useState<MoodboardFoto[]>([]);
  /** Firma del original por path: es lo que viaja en el adjunto. */
  const [originales, setOriginales] = useState<Map<string, string>>(new Map());
  const [cargandoFotos, setCargandoFotos] = useState(false);
  const [subiendo, setSubiendo] = useState(0);
  const [aBorrar, setABorrar] = useState<MoodboardFoto | null>(null);
  const [borrando, setBorrando] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const grillaRef = useRef<HTMLDivElement | null>(null);
  useReacomodoSuave(grillaRef);

  const avisar = (e: unknown, quePasaba: string) => {
    console.error(`[BibliotecaMoodboards] ${quePasaba}`, e);
    setError(e instanceof Error ? e.message : `No pudimos ${quePasaba}. Probá de nuevo.`);
  };

  const recargarCarpetas = async () => {
    if (!companyId) return;
    try {
      setCarpetas(await fetchMoodboards({ companyId, brandId }));
    } catch (e) {
      avisar(e, 'cargar las carpetas');
    }
  };

  useEffect(() => {
    if (!companyId || !marcaResuelta) return;
    let vivo = true;
    fetchMoodboards({ companyId, brandId })
      .then((lista) => {
        if (vivo) setCarpetas(lista);
      })
      .catch((e) => {
        if (!vivo) return;
        avisar(e, 'cargar las carpetas');
        setCarpetas([]);
      });
    return () => {
      vivo = false;
    };
  }, [companyId, brandId, marcaResuelta]);

  // Abrir una carpeta: las miniaturas (con transform) para la grilla y, en UN pedido,
  // la firma de los originales para los adjuntos.
  const abrir = async (carpeta: Moodboard) => {
    if (!companyId) return;
    setAbierta(carpeta);
    setFotos([]);
    setOriginales(new Map());
    setCargandoFotos(true);
    try {
      const lista = await fetchFotos({ companyId, moodboardId: carpeta.id });
      setFotos(lista);
      setOriginales(await firmarEnLote(lista.map((f) => f.path), MOODBOARDS_BUCKET));
    } catch (e) {
      avisar(e, 'cargar las fotos');
    } finally {
      setCargandoFotos(false);
    }
  };

  const volver = () => {
    setAbierta(null);
    setFotos([]);
    // La portada y el contador pueden haber cambiado adentro.
    void recargarCarpetas();
    onCarpetasCambiadas?.();
  };

  // Adjuntar la carpeta ENTERA: un puntero, sin vuelo (no hay una miniatura que mudar).
  const carpetaAdjunta = (c: Moodboard) => yaAdjunto(adjuntos, adjuntoDeCarpetaDeMoodboard(c));
  const adjuntarCarpeta = (c: Moodboard) =>
    onTomar({ adjunto: adjuntoDeCarpetaDeMoodboard(c), imageUrl: c.portada[0] ?? '' }, null);

  // De a una, como en Mi marca: cada foto entra a la grilla en cuanto termina.
  const agregarFotos = async (archivos: File[]) => {
    if (!companyId || !abierta) return;
    setError(null);
    setSubiendo((n) => n + archivos.length);
    for (const file of archivos) {
      try {
        const foto = await subirFoto({ companyId, moodboardId: abierta.id, file });
        const firma = await firmarEnLote([foto.path], MOODBOARDS_BUCKET);
        setOriginales((prev) => new Map([...prev, ...firma]));
        setFotos((previas) => [foto, ...previas]);
      } catch (e) {
        avisar(e, 'subir la foto');
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  };

  const elegirArchivos = (lista: FileList | null) => {
    const archivos = Array.from(lista ?? []).filter(
      (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name),
    );
    if (archivos.length) void agregarFotos(archivos);
  };

  const aviso = error && (
    <p role="status" className="mb-3 text-[12.5px] text-[#C33B36]">
      {error}
    </p>
  );

  if (!companyId || carpetas === null) {
    return <p className="py-8 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.45)]">Cargando…</p>;
  }

  // ── Nivel 1: las carpetas ──
  if (!abierta) {
    return (
      <div>
        {aviso}
        <GrillaDeCarpetas
          carpetas={carpetas}
          grillaClassName={GRILLA}
          onAbrir={(c) => void abrir(c)}
          onAdjuntar={adjuntarCarpeta}
          adjuntada={(id) => carpetas.some((c) => c.id === id && carpetaAdjunta(c))}
          onCrear={async (nombre) => {
            try {
              const carpeta = await crearMoodboard({ companyId, brandId, nombre, userId: usuarioId });
              setCarpetas((previas) => [carpeta, ...(previas ?? [])]);
              onCarpetasCambiadas?.();
            } catch (e) {
              avisar(e, 'crear la carpeta');
            }
          }}
          onRenombrar={async (id, nombre) => {
            const antes = carpetas;
            setCarpetas((previas) => (previas ?? []).map((c) => (c.id === id ? { ...c, nombre } : c)));
            try {
              await renombrarMoodboard(id, nombre);
              onCarpetasCambiadas?.();
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el nombre');
            }
          }}
          onCambiarColor={async (id, color) => {
            const antes = carpetas;
            setCarpetas((previas) => (previas ?? []).map((c) => (c.id === id ? { ...c, color } : c)));
            try {
              await cambiarColorDeMoodboard(id, color);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el color');
            }
          }}
          onEliminar={async (id) => {
            try {
              await eliminarMoodboard({ companyId, moodboardId: id });
              setCarpetas((previas) => (previas ?? []).filter((c) => c.id !== id));
              onCarpetasCambiadas?.();
            } catch (e) {
              avisar(e, 'eliminar la carpeta');
            }
          }}
        />
      </div>
    );
  }

  // ── Nivel 2: las fotos de una carpeta ──
  // Lo adjunto no está en la grilla: se mudó a la caja. Vuelve al sacar el chip.
  const fichas = fotos.flatMap((foto) => {
    const original = originales.get(foto.path);
    if (!original) return [];
    const adjunto = adjuntoDeMoodboard({ fotoId: foto.id, carpeta: abierta.nombre, imageUrl: original });
    // La miniatura de la grilla (con transform) y, si no llegó, el original.
    const miniatura = foto.thumbUrl || original;
    return yaAdjunto(adjuntos, adjunto) ? [] : [{ foto, adjunto, miniatura }];
  });

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={volver}
          className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          Volver
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-brand-black">{abierta.nombre}</p>
          <p className="text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
            {contador(fotos.length)}
            {subiendo > 0 && ` · subiendo ${subiendo}…`}
          </p>
        </div>
        {/* La carpeta entera al chat, desde adentro también. */}
        {carpetaAdjunta({ ...abierta, cantidadFotos: fotos.length }) ? (
          <span className="shrink-0 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.095)] px-2.5 py-1 text-[11.5px] font-semibold text-brand-primary">
            En el chat
          </span>
        ) : (
          <button
            type="button"
            onClick={() => adjuntarCarpeta({ ...abierta, cantidadFotos: fotos.length })}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 py-1.5 px-3 text-[12.5px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.9)] transition-colors hover:bg-neutral-200"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            Adjuntar carpeta
          </button>
        )}
      </div>
      {aviso}

      <input
        ref={inputRef}
        type="file"
        accept={FORMATOS}
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          elegirArchivos(e.target.files);
          e.target.value = '';
        }}
      />

      {cargandoFotos ? (
        <p className="py-8 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.45)]">Cargando…</p>
      ) : (
        <div ref={grillaRef} className={GRILLA}>
          <AccionCard
            className="aspect-square w-full"
            label="Agregar fotos"
            sublabel="Referencias de estilo"
            icon={subiendo > 0 ? <ImagePlus className="h-5 w-5" /> : <Plus className="h-5 w-5" strokeWidth={2.5} />}
            loading={subiendo > 0}
            loadingLabel={`Subiendo ${subiendo}…`}
            onClick={() => {
              setError(null);
              inputRef.current?.click();
            }}
          />
          {/* Las que están subiendo, adelante: aparecen donde van a quedar. */}
          {Array.from({ length: subiendo }, (_, i) => (
            <div
              key={`subiendo-${i}`}
              className="flex aspect-square items-center justify-center rounded-xl bg-[#F0F2F7]"
            >
              <Loader2 className="h-4 w-4 animate-spin text-[rgb(var(--brand-black-rgb)_/_0.3)]" />
            </div>
          ))}
          {fichas.map(({ foto, adjunto, miniatura }) => (
            <div key={foto.id} {...{ [ATRIBUTO_FICHA]: adjunto.id }} className="group relative">
              <button
                type="button"
                onClick={(e) =>
                  onTomar(
                    { adjunto, imageUrl: miniatura },
                    e.currentTarget.querySelector<HTMLElement>('[data-miniatura]'),
                  )
                }
                className="block w-full text-left"
              >
                <span
                  data-miniatura=""
                  className="relative block aspect-square w-full overflow-hidden rounded-xl bg-[#F0F2F7] ring-2 ring-transparent ring-offset-2 transition-shadow group-hover:ring-[rgb(var(--brand-black-rgb)_/_0.12)]"
                >
                  <CachedImg src={miniatura} alt="" className="h-full w-full object-cover" />
                </span>
              </button>
              {/* Borrar: como en Mi marca, aparece al pasar el mouse. */}
              <button
                type="button"
                onClick={() => setABorrar(foto)}
                aria-label="Eliminar foto"
                className="absolute right-1.5 top-1.5 z-[2] inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[rgb(var(--brand-black-rgb)_/_0.7)] opacity-0 shadow-sm backdrop-blur transition-all hover:text-red-600 group-hover:opacity-100 group-focus-within:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {!cargandoFotos && fichas.length === 0 && subiendo === 0 && (
        <p className="pt-4 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.45)]">
          {fotos.length > 0 ? 'Ya está todo en el chat.' : 'Esta carpeta todavía no tiene fotos.'}
        </p>
      )}

      <ModalConfirmar
        abierto={aBorrar !== null}
        titulo="Eliminar la foto"
        detalle="Se saca de esta carpeta. No se puede deshacer."
        confirmar="Eliminar foto"
        trabajando={borrando}
        onCancelar={() => setABorrar(null)}
        onConfirmar={async () => {
          if (!aBorrar) return;
          setBorrando(true);
          try {
            await eliminarFoto({ fotoId: aBorrar.id, path: aBorrar.path });
            setFotos((previas) => previas.filter((f) => f.id !== aBorrar.id));
            setABorrar(null);
          } catch (e) {
            avisar(e, 'eliminar la foto');
          } finally {
            setBorrando(false);
          }
        }}
      />
    </div>
  );
};

export default BibliotecaMoodboards;
